/**
 * useUploadCapabilities Hook
 *
 * Fetches the server's env-driven upload allow-list from
 * GET /api/documents/upload/capabilities so the upload dialog's accept filter
 * and the "supported formats" hint stay in sync with the backend (which gates
 * image types behind IMAGE_UPLOAD_ENABLED) instead of hardcoding a list that
 * can drift. Called via the raw apiClient.request since this additive endpoint
 * is not part of the regenerated typed client (regenerating would drop the
 * hand-maintained chat types).
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../../config/apiConfig';

export interface UploadCapabilities {
  allowed_extensions: string[];
  /** Comma-joined extension list ready for an <input accept="..."> attribute. */
  accept: string;
  image_upload_enabled: boolean;
  max_file_size_mb: number;
}

// Safe defaults used while the query is loading or if it fails: the prior
// always-available formats, no images. The backend remains the real gate.
const DEFAULT_CAPABILITIES: UploadCapabilities = {
  allowed_extensions: ['.pdf', '.docx', '.pptx', '.xlsx', '.txt', '.md', '.vtt', '.srt', '.eml', '.msg'],
  accept: '.pdf,.docx,.pptx,.xlsx,.txt,.md,.vtt,.srt,.eml,.msg',
  image_upload_enabled: false,
  max_file_size_mb: 10,
};

export const uploadCapabilitiesQueryKey = ['documents', 'upload-capabilities'] as const;

export function useUploadCapabilities(): {
  capabilities: UploadCapabilities;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: uploadCapabilitiesQueryKey,
    queryFn: async (): Promise<UploadCapabilities> => {
      const res = await apiClient.request<UploadCapabilities>({
        path: '/api/documents/upload/capabilities',
        method: 'GET',
        secure: true,
        format: 'json',
      });
      return res.data;
    },
    // Capabilities are env-driven and change only on redeploy, so cache hard.
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
    retry: 1,
  });

  return {
    capabilities: data ?? DEFAULT_CAPABILITIES,
    isLoading,
  };
}
